// =============================================================================
// Dashboard Page - Statistiche e KPI Principale
// =============================================================================

import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Calendar,
  DollarSign,
  Star,
  TrendingUp,
  Clock,
  Loader2,
  AlertCircle,
  AlertTriangle,
  ChevronRight,
  BarChart3,
  Users,
  CheckCircle,
  XCircle,
  Gauge,
} from 'lucide-react';
import {
  ComposedChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from 'recharts';
import { EmptyState } from '../components/common';
import { useDashboardStats, useDashboardUtilization, useReorganizationNeeded } from '../api/hooks';
import { useSchedulerActions } from '../store/schedulerStore';
import { useTranslation } from 'react-i18next';
import { useDateLocale } from '../hooks/useDateLocale';
import { useTenantTimeZone } from '../store/tenantStore';
import { formatInTz, timeStringInTz } from '../lib/tenantTime';
import { cn } from '../lib/utils';
import {
  APPOINTMENT_STATUS_COLORS,
  APPOINTMENT_STATUS_LABEL_KEYS,
} from '../types';
import type { AppointmentStatus } from '../types';

// ===========================================================================
// Period Selector
// ===========================================================================

const PERIODS = [
  { value: 7, labelKey: 'periods.last7days' },
  { value: 30, labelKey: 'periods.last30days' },
  { value: 90, labelKey: 'periods.last90days' },
];

// ===========================================================================
// KPI Card Component
// ===========================================================================

interface KpiCardProps {
  title: string;
  value: string | number;
  subtitle?: string;
  icon: React.ElementType;
  iconColor: string;
  iconBg: string;
  trend?: string;
  trendPositive?: boolean;
}

function KpiCard({ title, value, subtitle, icon: Icon, iconColor, iconBg, trend, trendPositive }: KpiCardProps) {
  return (
    <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
      <div className="flex items-start justify-between">
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-gray-500 truncate">{title}</p>
          <p className="mt-2 text-3xl font-bold text-gray-900 tracking-tight">{value}</p>
          {subtitle && (
            <p className="mt-1 text-sm text-gray-500 truncate">{subtitle}</p>
          )}
        </div>
        <div className="flex flex-col items-end gap-2">
          <div className={cn('p-2.5 rounded-lg', iconBg)}>
            <Icon className={cn('w-5 h-5', iconColor)} />
          </div>
          {trend && (
            <span
              className={cn(
                'inline-flex items-center gap-0.5 text-xs font-semibold px-2 py-0.5 rounded-full',
                trendPositive
                  ? 'bg-emerald-50 text-emerald-600'
                  : 'bg-red-50 text-red-600'
              )}
            >
              <TrendingUp className={cn('w-3 h-3', !trendPositive && 'rotate-180')} />
              {trend}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

// ===========================================================================
// Chart Tooltip
// ===========================================================================

function CustomTooltip({ active, payload, label }: any) {
  const { t } = useTranslation('dashboard');
  if (!active || !payload?.length) return null;

  return (
    <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-3 text-sm">
      <p className="font-semibold text-gray-900 mb-1.5">{label}</p>
      {payload.map((entry: any, index: number) => (
        <p key={index} className="flex items-center gap-2 text-gray-500 leading-relaxed">
          <span
            className="inline-block w-2 h-2 rounded-full flex-shrink-0"
            style={{ backgroundColor: entry.color }}
          />
          {entry.name === 'count' ? t('chart.appointmentsLegend') : t('chart.revenueLegend')}:{' '}
          <span className="font-medium text-gray-900">
            {entry.name === 'revenue' ? `€${entry.value.toFixed(2)}` : entry.value}
          </span>
        </p>
      ))}
    </div>
  );
}

// ===========================================================================
// Utilization Tooltip
// ===========================================================================

function UtilizationTooltip({ active, payload, label }: any) {
  if (!active || !payload?.length) return null;

  return (
    <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-3 text-sm">
      <p className="font-semibold text-gray-900 mb-1.5">{label}</p>
      {payload.map((entry: any, index: number) => (
        <p key={index} className="flex items-center gap-2 text-gray-500 leading-relaxed">
          <span
            className="inline-block w-2 h-2 rounded-full flex-shrink-0"
            style={{ backgroundColor: entry.color }}
          />
          {entry.name}:{' '}
          <span className="font-medium text-gray-900">{entry.value}%</span>
        </p>
      ))}
    </div>
  );
}

// ===========================================================================
// Status Badge
// ===========================================================================

function StatusBadge({ status }: { status: string }) {
  const { t } = useTranslation('common');
  const colorClass = APPOINTMENT_STATUS_COLORS[status as AppointmentStatus] || 'bg-gray-100 text-gray-700';
  const labelKey = APPOINTMENT_STATUS_LABEL_KEYS[status as AppointmentStatus];
  const label = labelKey ? t(labelKey) : status;

  return (
    <span className={cn('inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium', colorClass)}>
      {label}
    </span>
  );
}

// ===========================================================================
// Main Dashboard Page
// ===========================================================================

export function DashboardPage() {
  const { t, i18n: i18nInstance } = useTranslation('dashboard');
  const dateLocale = useDateLocale();
  const timeZone = useTenantTimeZone();
  const [days, setDays] = useState(30);
  const { data: stats, isLoading, isError, error } = useDashboardStats(days);
  const { data: utilization, isLoading: isLoadingUtil } = useDashboardUtilization();
  const { data: reorganization } = useReorganizationNeeded();
  const navigate = useNavigate();
  const { setDate } = useSchedulerActions();

  // Naviga al calendario aprendolo sul giorno dell'appuntamento
  const goToAppointmentDay = (dateStr: string) => {
    setDate(new Date(dateStr));
    navigate('/scheduler');
  };

  // Formatta fatturato
  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat(i18nInstance.language === 'it' ? 'it-IT' : 'en-US', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  // Formatta data/ora nel fuso dell'organizzazione: la dashboard descrive la
  // giornata dell'attivita', non quella del browser di chi la apre.
  const formatDateTime = (dateStr: string) => {
    const d = new Date(dateStr);
    return {
      date: formatInTz(d, timeZone, 'dd MMM', dateLocale),
      time: timeStringInTz(d, timeZone),
    };
  };

  // Loading state
  if (isLoading) {
    return (
    <div className="h-full flex flex-col bg-gray-50">
      <DashboardHeader days={days} onDaysChange={setDays} />
        <div className="flex-1 flex items-center justify-center">
          <div className="flex flex-col items-center gap-3">
            <Loader2 className="w-8 h-8 animate-spin text-emerald-500" />
            <p className="text-sm text-gray-500">{t('loading')}</p>
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (isError) {
    return (
      <div className="h-full flex flex-col bg-gray-50">
        <DashboardHeader days={days} onDaysChange={setDays} />
        <div className="flex-1 flex items-center justify-center">
          <div className="flex flex-col items-center gap-3 text-center max-w-md">
            <AlertCircle className="w-12 h-12 text-red-500" />
            <p className="text-gray-900 font-medium">{t('errorTitle')}</p>
            <p className="text-sm text-gray-500">
              {(error as any)?.message || t('errorMessage')}
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Formatta trend da backend
  const formatTrend = (value: number | null) => {
    if (value === null) return undefined;
    return { text: `${value >= 0 ? '+' : ''}${value}%`, positive: value >= 0 };
  };

  const aptTrend = formatTrend(stats?.trends?.appointmentsTrend ?? null);
  const revTrend = formatTrend(stats?.trends?.revenueTrend ?? null);

  // Saturazione media risorse (previsionale) — metrica operations
  const avgSaturation =
    utilization && utilization.length > 0
      ? Math.round(
          utilization.reduce((s, u) => s + u.futureUtilization, 0) / utilization.length,
        )
      : null;

  return (
    <div className="h-full flex flex-col bg-gray-50">
      <DashboardHeader days={days} onDaysChange={setDays} />

      {/* Content */}
      <main className="flex-1 overflow-auto p-6">
        <div className="space-y-6">
          {/* ===== KPI Cards Row ===== */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
            <KpiCard
              title={t('kpi.appointments')}
              value={stats?.totalAppointments ?? 0}
              subtitle={t('kpi.appointmentsSubtitle')}
              icon={Calendar}
              iconColor="text-emerald-500"
              iconBg="bg-emerald-50"
              trend={aptTrend?.text}
              trendPositive={aptTrend?.positive}
            />
            <KpiCard
              title={t('kpi.revenue')}
              value={formatCurrency(stats?.totalRevenue ?? 0)}
              subtitle={t('kpi.revenueSubtitle')}
              icon={DollarSign}
              iconColor="text-emerald-500"
              iconBg="bg-emerald-50"
              trend={revTrend?.text}
              trendPositive={revTrend?.positive}
            />
            <KpiCard
              title={t('kpi.saturation')}
              value={avgSaturation !== null ? `${avgSaturation}%` : '—'}
              subtitle={t('kpi.saturationSubtitle')}
              icon={Gauge}
              iconColor="text-emerald-500"
              iconBg="bg-emerald-50"
            />
            <KpiCard
              title={t('kpi.completed')}
              value={stats?.completedCount ?? 0}
              subtitle={t('kpi.completedSubtitle')}
              icon={CheckCircle}
              iconColor="text-emerald-500"
              iconBg="bg-emerald-50"
            />
            <KpiCard
              title={t('kpi.cancelled')}
              value={stats?.cancelledCount ?? 0}
              subtitle={t('kpi.cancelledSubtitle')}
              icon={XCircle}
              iconColor="text-red-500"
              iconBg="bg-red-50"
            />
          </div>

          {/* ===== Appuntamenti da riorganizzare (assenze risorse) ===== */}
          {reorganization && reorganization.length > 0 && (
            <div className="bg-white border border-amber-300 rounded-xl shadow-sm overflow-hidden">
              <div className="flex items-center gap-2 px-6 py-4 bg-amber-50 border-b border-amber-200">
                <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0" />
                <div className="min-w-0">
                  <h3 className="text-sm font-semibold text-amber-800">
                    {t('reorganization.title', { count: reorganization.length })}
                  </h3>
                  <p className="text-xs text-amber-700">{t('reorganization.subtitle')}</p>
                </div>
              </div>
              <ul className="divide-y divide-gray-100">
                {reorganization.map((item) => {
                  const { date, time } = formatDateTime(item.scheduledStart);
                  return (
                    <li key={item.allocationId}>
                      <button
                        onClick={() => goToAppointmentDay(item.scheduledStart)}
                        className="w-full flex items-center gap-4 px-6 py-3 text-left hover:bg-amber-50/60 transition-colors group"
                        title={t('reorganization.goToDay')}
                      >
                        <div className="w-16 flex-shrink-0">
                          <p className="font-medium text-gray-900 text-sm">{date}</p>
                          <p className="text-xs text-gray-500">{time}</p>
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm text-gray-900 truncate">
                            {item.serviceName || item.title}
                            {item.customerName && (
                              <span className="text-gray-500"> — {item.customerName}</span>
                            )}
                          </p>
                          <p className="text-xs text-gray-500 truncate flex items-center gap-1.5 mt-0.5">
                            <span
                              className="w-2 h-2 rounded-full flex-shrink-0"
                              style={{ backgroundColor: item.resourceColor || '#6B7280' }}
                            />
                            {t('reorganization.resourceAbsent', {
                              resource: item.resourceName,
                              reason: item.absenceTitle,
                            })}
                          </p>
                        </div>
                        <ChevronRight className="w-4 h-4 text-gray-300 group-hover:text-amber-600 transition-colors flex-shrink-0" />
                      </button>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}

          {/* ===== Resource Utilization Section ===== */}
          <div className="bg-white border border-gray-200 rounded-xl shadow-sm">
            <div className="px-6 pt-6 pb-2">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-sm font-medium text-gray-500 flex items-center gap-2">
                    <Users className="w-4 h-4 text-emerald-500" />
                    {t('utilization.title')}
                  </h3>
                  <p className="text-xs text-gray-400 mt-0.5">
                    {t('utilization.subtitle')}
                  </p>
                </div>
                <div className="flex items-center gap-4 text-xs text-gray-500">
                  <span className="flex items-center gap-1.5">
                    <span className="w-2.5 h-2.5 rounded-sm" style={{ backgroundColor: '#94a3b8' }} />
                    {t('utilization.historical')}
                  </span>
                  <span className="flex items-center gap-1.5">
                    <span className="w-2.5 h-2.5 rounded-sm" style={{ backgroundColor: '#10b981' }} />
                    {t('utilization.forecast')}
                  </span>
                </div>
              </div>
            </div>

            <div className="px-6 pb-6 pt-2">
              {isLoadingUtil ? (
                <div className="h-[300px] flex items-center justify-center">
                  <Loader2 className="w-6 h-6 animate-spin text-emerald-500" />
                </div>
              ) : utilization && utilization.length > 0 ? (
                <ResponsiveContainer width="100%" height={Math.max(200, utilization.length * 52 + 40)}>
                  <ComposedChart
                    data={utilization}
                    layout="vertical"
                    margin={{ top: 4, right: 20, left: 0, bottom: 4 }}
                    barGap={2}
                    barCategoryGap="20%"
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" horizontal={false} />
                    <XAxis
                      type="number"
                      domain={[0, 100]}
                      axisLine={false}
                      tickLine={false}
                      tick={{ fill: '#64748b', fontSize: 12 }}
                      tickFormatter={(v: number) => `${v}%`}
                    />
                    <YAxis
                      type="category"
                      dataKey="name"
                      axisLine={false}
                      tickLine={false}
                      tick={{ fill: '#64748b', fontSize: 12 }}
                      width={120}
                    />
                    <Tooltip content={<UtilizationTooltip />} cursor={{ fill: '#f8fafc' }} />
                    <Bar
                      dataKey="pastUtilization"
                      name={t('utilization.historical')}
                      fill="#94a3b8"
                      radius={[0, 4, 4, 0]}
                      maxBarSize={18}
                    />
                    <Bar
                      dataKey="futureUtilization"
                      name={t('utilization.forecast')}
                      radius={[0, 4, 4, 0]}
                      maxBarSize={18}
                    >
                      {utilization!.map((entry) => (
                        <Cell key={entry.resourceId} fill={entry.color || '#10b981'} />
                      ))}
                    </Bar>
                  </ComposedChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-[200px] flex items-center justify-center">
                  <EmptyState icon={Users} title={t('utilization.noData')} size="sm" />
                </div>
              )}
            </div>
          </div>

          {/* ===== Chart + Status Breakdown Row ===== */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* Chart */}
          <div className="lg:col-span-2 bg-white border border-gray-200 rounded-xl shadow-sm">
            <div className="px-6 pt-6 pb-2">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-sm font-medium text-gray-500 flex items-center gap-2">
                    <BarChart3 className="w-4 h-4" />
                    Andamento {stats?.period?.label ?? `Ultimi ${days} Giorni`}
                  </h3>
                  <p className="text-xs text-gray-400 mt-0.5">
                    {t('chart.subtitle')}
                  </p>
                </div>
                {/* Chart Legend */}
                <div className="flex items-center gap-4 text-xs text-gray-500">
                  <span className="flex items-center gap-1.5">
                    <span className="w-2.5 h-2.5 rounded-sm bg-emerald-500" />
                    {t('chart.appointmentsLegend')}
                  </span>
                  <span className="flex items-center gap-1.5">
                    <span className="w-3 h-0.5 rounded-full bg-emerald-700" />
                    {t('chart.revenueLegend')}
                  </span>
                </div>
              </div>
            </div>

            <div className="px-6 pb-6 pt-2">
              {stats?.chartData && stats.chartData.length > 0 ? (
                <ResponsiveContainer width="100%" height={280}>
                  <ComposedChart data={stats.chartData} margin={{ top: 8, right: 4, left: -12, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                    <XAxis
                      dataKey="date"
                      axisLine={false}
                      tickLine={false}
                      tick={{ fill: '#64748b', fontSize: 12 }}
                      dy={8}
                    />
                    {/* Asse sinistro: conteggio appuntamenti (barre) */}
                    <YAxis
                      yAxisId="left"
                      axisLine={false}
                      tickLine={false}
                      tick={{ fill: '#64748b', fontSize: 12 }}
                      width={32}
                      allowDecimals={false}
                    />
                    {/* Asse destro: fatturato € (linea) */}
                    <YAxis
                      yAxisId="right"
                      orientation="right"
                      axisLine={false}
                      tickLine={false}
                      tick={{ fill: '#94a3b8', fontSize: 11 }}
                      width={48}
                      tickFormatter={(v: number) => `€${v}`}
                    />
                    <Tooltip content={<CustomTooltip />} cursor={{ fill: '#f8fafc' }} />
                    <Bar
                      yAxisId="left"
                      dataKey="count"
                      name="count"
                      fill="#10b981"
                      radius={[4, 4, 0, 0]}
                      maxBarSize={32}
                    />
                    <Line
                      yAxisId="right"
                      type="monotone"
                      dataKey="revenue"
                      name="revenue"
                      stroke="#047857"
                      strokeWidth={2}
                      dot={{ r: 3, fill: '#047857' }}
                      activeDot={{ r: 5 }}
                    />
                  </ComposedChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-[280px] flex items-center justify-center">
                  <EmptyState icon={BarChart3} title={t('chart.noData')} size="sm" />
                </div>
              )}
            </div>
          </div>

          {/* Status Breakdown */}
          <StatusBreakdownCard breakdown={stats?.statusBreakdown ?? []} />
          </div>

          {/* ===== Top Services + Upcoming Row ===== */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            {/* Top Services */}
            <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-6">
              <h3 className="text-sm font-medium text-gray-500 flex items-center gap-2 mb-5">
                <Star className="w-4 h-4 text-emerald-500" />
                {t('topServices.title')}
              </h3>
              {stats?.topServices && stats.topServices.length > 0 ? (
                <div className="space-y-4">
                  {stats.topServices.map((service) => {
                    const maxCount = stats.topServices[0]?.count || 1;
                    const percentage = Math.round((service.count / maxCount) * 100);
                    const barColor = service.color || '#10b981';
                    return (
                      <div key={service.serviceId}>
                        <div className="flex items-center justify-between mb-1.5">
                          <span className="text-sm text-gray-900 truncate">
                            {service.serviceName}
                          </span>
                          <span className="text-sm font-semibold text-gray-900 ml-3 tabular-nums">
                            {service.count}
                          </span>
                        </div>
                        <div className="w-full bg-gray-100 rounded-full h-1.5">
                          <div
                            className="h-1.5 rounded-full transition-all duration-500"
                            style={{ width: `${percentage}%`, backgroundColor: barColor }}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="text-sm text-gray-400 text-center py-8">
                  {t('topServices.noData')}
                </p>
              )}
            </div>

            {/* Upcoming Appointments */}
            <div className="lg:col-span-2 bg-white border border-gray-200 rounded-xl shadow-sm">
              <div className="flex items-center justify-between px-6 py-4">
                <h3 className="text-sm font-medium text-gray-500 flex items-center gap-2">
                  <Clock className="w-4 h-4 text-emerald-500" />
                  {t('upcoming.title')}
                </h3>
                <Link
                  to="/scheduler"
                  className="text-xs font-medium text-emerald-600 hover:text-emerald-700 flex items-center gap-0.5 transition-colors"
                >
                  {t('upcoming.viewAll')}
                  <ChevronRight className="w-3 h-3" />
                </Link>
              </div>

              {stats?.upcomingAppointments && stats.upcomingAppointments.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-gray-100">
                        <th className="text-left px-6 py-2.5 text-xs font-medium text-gray-500 uppercase tracking-wider">
                          {t('upcoming.dateTime')}
                        </th>
                        <th className="text-left px-6 py-2.5 text-xs font-medium text-gray-500 uppercase tracking-wider">
                          {t('upcoming.customer')}
                        </th>
                        <th className="text-left px-6 py-2.5 text-xs font-medium text-gray-500 uppercase tracking-wider">
                          {t('upcoming.service')}
                        </th>
                        <th className="text-left px-6 py-2.5 text-xs font-medium text-gray-500 uppercase tracking-wider">
                          {t('upcoming.price')}
                        </th>
                        <th className="text-left px-6 py-2.5 text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Stato
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {stats.upcomingAppointments.map((apt) => {
                        const { date, time } = formatDateTime(apt.scheduledStart);
                        return (
                          <tr
                            key={apt.id}
                            className="border-b border-gray-100 last:border-b-0 hover:bg-gray-50 transition-colors"
                          >
                            <td className="px-6 py-3">
                              <p className="font-medium text-gray-900">{date}</p>
                              <p className="text-xs text-gray-500">{time}</p>
                            </td>
                            <td className="px-6 py-3 text-gray-900">
                              {apt.customer
                                ? `${apt.customer.firstName} ${apt.customer.lastName}`
                                : <span className="text-gray-400 italic">{t('upcoming.na')}</span>}
                            </td>
                            <td className="px-6 py-3 text-gray-500">
                              {apt.service?.name || apt.title}
                            </td>
                            <td className="px-6 py-3 font-medium text-gray-900 tabular-nums">
                              €{apt.price.toFixed(2)}
                            </td>
                            <td className="px-6 py-3">
                              <StatusBadge status={apt.status} />
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              ) : (
                <EmptyState icon={Calendar} title={t('upcoming.noData')} className="py-12" />
              )}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}

// ===========================================================================
// Status Breakdown Card
// ===========================================================================

const STATUS_PIE_COLORS: Record<string, string> = {
  CONFIRMED: '#10b981',
  PENDING: '#f59e0b',
  COMPLETED: '#6b7280',
  CANCELLED: '#ef4444',
  IN_PROGRESS: '#3b82f6',
  DRAFT: '#9ca3af',
  NO_SHOW: '#f97316',
  RESCHEDULED: '#8b5cf6',
};

function StatusBreakdownCard({ breakdown }: { breakdown: Array<{ status: string; count: number }> }) {
  const { t } = useTranslation('dashboard');
  const total = breakdown.reduce((sum, b) => sum + b.count, 0);

  if (!breakdown.length || total === 0) {
    return (
      <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-6 flex items-center justify-center">
        <p className="text-sm text-gray-400">{t('statusBreakdown.noData')}</p>
      </div>
    );
  }

  return (
    <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-6">
      <h3 className="text-sm font-medium text-gray-500 mb-4">{t('statusBreakdown.title')}</h3>

      {/* Mini pie chart */}
      <div className="flex justify-center mb-4">
        <ResponsiveContainer width={140} height={140}>
          <PieChart>
            <Pie
              data={breakdown}
              dataKey="count"
              nameKey="status"
              cx="50%"
              cy="50%"
              innerRadius={35}
              outerRadius={60}
              paddingAngle={2}
              strokeWidth={0}
            >
              {breakdown.map((entry) => (
                <Cell key={entry.status} fill={STATUS_PIE_COLORS[entry.status] || '#d1d5db'} />
              ))}
            </Pie>
            <Tooltip
              formatter={(value: any, name: any) => {
                const labelKey = APPOINTMENT_STATUS_LABEL_KEYS[name as AppointmentStatus];
                return [value, labelKey ? t(`common:${labelKey}`) : name];
              }}
            />
          </PieChart>
        </ResponsiveContainer>
      </div>

      {/* Legend list */}
      <div className="space-y-2">
        {breakdown.map((entry) => {
          const pct = Math.round((entry.count / total) * 100);
          const color = STATUS_PIE_COLORS[entry.status] || '#d1d5db';
          const labelKey = APPOINTMENT_STATUS_LABEL_KEYS[entry.status as AppointmentStatus];
          const label = labelKey ? t(`common:${labelKey}`) : entry.status;
          return (
            <div key={entry.status} className="flex items-center justify-between text-sm">
              <span className="flex items-center gap-2 text-gray-600">
                <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: color }} />
                {label}
              </span>
              <span className="font-medium text-gray-900 tabular-nums">
                {entry.count} <span className="text-gray-400 text-xs">({pct}%)</span>
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ===========================================================================
// Dashboard Header
// ===========================================================================

interface DashboardHeaderProps {
  days: number;
  onDaysChange: (days: number) => void;
}

function DashboardHeader({ days, onDaysChange }: DashboardHeaderProps) {
  const { t } = useTranslation('dashboard');
  return (
    <header className="bg-white border-b border-gray-200 px-6 py-4 flex-shrink-0">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-emerald-50 rounded-lg">
            <BarChart3 className="w-5 h-5 text-emerald-600" />
          </div>
          <h1 className="text-lg font-semibold text-gray-900">Dashboard</h1>
        </div>

        <div className="flex items-center gap-1.5">
          {PERIODS.map((period) => (
            <button
              key={period.value}
              onClick={() => onDaysChange(period.value)}
              className={cn(
                'px-3 py-1.5 text-sm rounded-lg font-medium transition-colors',
                days === period.value
                  ? 'bg-emerald-600 text-white'
                  : 'text-gray-600 hover:bg-gray-100'
              )}
            >
              {t(period.labelKey)}
            </button>
          ))}
        </div>
      </div>
    </header>
  );
}

export default DashboardPage;
